import { useEffect, useRef, useState } from 'react';
import './Layout.scss';
import notification from '../assets/image/notification.svg';
import logoutIcon from '../assets/image/logout-red-icon.svg';
import Button from '../component/ui/Button';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Moment from 'react-moment';
import Clock from 'react-live-clock';
import { useDispatch, useSelector } from 'react-redux';
import { clearUserInfo, setAuthToken, setIsOnboardingCompleted } from '../store/action/userInfo';
import GlobalSearch from '../component/globalSearch/GlobalSearch';
import collapse from '../assets/image/sidebar/collapseSidebar.svg';
import logo from '../assets/image/PharmaNex_small.svg';
import expand from '../assets/image/sidebar/expandSidebar.svg';
import { toast } from 'react-toastify';
import { getNotificationsUnreadCounts, updateStatusNotification } from '../services/dashboard';
import { useSocket } from '../context/socketContext';
import { formatTimeAgo } from '../pages/dashboard/utils';
import {
	clearNotification,
	updateNotifications,
	updateRecentNotifications,
} from '../store/action/notifications';
import QuickAction from '../pages/components/quickAction';
import { Gem } from 'lucide-react';
import PricingModal from '../component/PricingModal';
import { getSubscriptions } from '../services/settings/subscriptions';
import PageLoader from '../component/PageLoader';

const Navbar = ({ collapsed, setCollapsed, inputRef }) => {
	const [isOpenUpgradeModal, setIsOpenUpgradeModal] = useState(false);
	const [isloadSubscription, setIsLoadSubscription] = useState(false);
	const [subscriptionDetails, setSubscriptionDetails] = useState({});

	const queueRef = useRef([]);
	const socket = useSocket();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const params = useParams();
	const location = useLocation();
	const { recent_notification: notifications, total_unread_counts: unreadCount } = useSelector(
		(state) => state.notifications,
	);
	const [isOpenNotificationModal, setIsOpenNotificationModal] = useState(false);
	const notificationRef = useRef(null);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (notificationRef.current && !notificationRef.current.contains(event.target)) {
				setIsOpenNotificationModal(false);
			}
		};

		if (isOpenNotificationModal) {
			document.addEventListener('mousedown', handleClickOutside);
		}
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isOpenNotificationModal]);
	const userDetails = useSelector((state) => state?.userInfo?.userInfo);
	const suburl = localStorage.getItem('suburl');

	// The old template literal interpolated first_name/last_name directly, so
	// before /user/info resolves (or for a user with no last name) the header
	// read "Welcome! undefined undefined". Filtering the parts keeps it to
	// whatever is actually known.
	const fullName = [userDetails?.data?.first_name, userDetails?.data?.last_name]
		.filter(Boolean)
		.join(' ');
	const welcomeName = fullName ? `Welcome! ${fullName}` : 'Welcome!';

	const handleSignout = () => {
		localStorage.clear();
		dispatch(clearNotification());
		dispatch(setAuthToken({ authToken: null }));
		dispatch(clearUserInfo());
		dispatch(setIsOnboardingCompleted({ isOnboardingCompleted: false }));
		const extractSubDomainFromPath = (pathname) => pathname.match(/^\/user\/([^/]+)/)?.[1] || null;
		const subDomain = extractSubDomainFromPath(location.pathname);
		if (!subDomain) {
			navigate(`/`);
		} else {
			navigate(`/user/${subDomain}`);
		}
	};

	const shortCutNavigate = (path) => {
		navigate(path);
	};

	const handlehamburger = () => {
		let root = document.getElementById('root');
		root.classList.toggle('collapsed');
		setCollapsed(!collapsed);
	};

	const markReadNotification = async (ids = []) => {
		if (ids.length > 0) {
			try {
				const body = {
					status: 'is_read',
					value: true,
					ids: ids,
					mark_as_read: false,
				};

				await updateStatusNotification(body);
				dispatch(
					updateRecentNotifications({
						ids: ids,
					}),
				);
			} catch (error) {
				toast.error(error.response.message || 'Somenthing went wrong');
			}
		}
	};

	// fetchUserInfo() used to be dispatched here too, on a 300ms setTimeout,
	// fire-and-forget. That left a real window where Navbar (and every
	// hasPermission() check in the modules it sits above) could render
	// BEFORE the fetch resolved — using whatever `userInfo.data` redux-persist
	// had rehydrated, which is empty on a first-ever login and stale after a
	// refresh on any page that never independently re-fetched it. It's now
	// fetched once, authoritatively, in PrivateRoutes.js before Layout (and
	// so Navbar) ever mounts — see the comment there. `suburl` in localStorage
	// (used a few lines below) is already set at login by useAuth.js, so
	// dropping this doesn't lose that.

	useEffect(() => {
		async function handleFetchUnreadNotificationsCounts() {
			try {
				const { recent_notifications, unread_counts } = await getNotificationsUnreadCounts();
				dispatch(
					updateRecentNotifications({
						recent_notification: recent_notifications,
						total_unread_counts: unread_counts,
					}),
				);
			} catch (error) {}
		}
		handleFetchUnreadNotificationsCounts();
	}, []);

	useEffect(() => {
		if (!socket) return;

		const handleNotification = (data) => {
			if (data && data.id) {
				queueRef.current.push(data);
			}
		};

		socket.on('notification', handleNotification);

		const interval = setInterval(() => {
			if (queueRef.current.length > 0) {
				const newNotificationBatch = queueRef.current;
				queueRef.current = [];

				const merged = [...newNotificationBatch, ...notifications];
				const unique = merged.filter((v, i, a) => a.findIndex((t) => t.id === v.id) === i);
				const recent_notification = unique.slice(0, 5);

				dispatch(
					updateNotifications({
						notifications: newNotificationBatch,
						recent_notification: recent_notification,
						total_unread_counts: newNotificationBatch.length,
					}),
				);
			}
		}, 500);

		return () => {
			socket.off('notification', handleNotification);
			clearInterval(interval);
		};
	}, [socket, dispatch]);

	const getSubscriptionDetails = async () => {
		try {
			setIsLoadSubscription(true);
			const response = await getSubscriptions();
			setSubscriptionDetails(response?.subscription ?? {});
		} catch (error) {
			console.error('Failed to load subscription details:', error);
			setSubscriptionDetails({});
		} finally {
			setIsLoadSubscription(false);
		}
	};

	useEffect(() => {
		getSubscriptionDetails();
	}, []);

	return (
		<div>
			<PricingModal
				isOpen={isOpenUpgradeModal}
				handleClose={() => setIsOpenUpgradeModal(false)}
				onPlanChanged={getSubscriptionDetails}
			/>
			<div className={collapsed ? 'navtop' : 'navtop'}>
				<div className="nav-container">
					<span className="align-items-center d-flex gap-4">
						<img
							src={collapse}
							width="20"
							alt=""
							onClick={handlehamburger}
						/>
						<GlobalSearch inputRef={inputRef} />
					</span>

					<div className="right-details">
						{/* Name on the first line, date/time on the second. These were two
						    siblings that .right-details laid out side by side; wrapping them
						    stacks the pair as one unit and keeps the row's single 20px gap
						    to the icons beside it. */}
						<Button
							className="mt-0 btn-primary fs-5 fw-light add-pro py-1"
							onClick={() =>
								window.open('https://pos.pharmnex.app', '_blank', 'noopener,noreferrer')
							}
							button="POS"
						/>

						<Button
							type="button"
							title="Upgrade Plan"
							className="mt-0 btn-primary fs-5 fw-light add-pro py-1 navbar-upgrade-btn"
							button={
								<span className="d-flex align-items-center gap-2">
									<Gem
										size={18}
										strokeWidth={2}
									/>
									<span className="upgrade-btn-text">Upgrade Plan</span>
								</span>
							}
							onClick={() => setIsOpenUpgradeModal(true)}
						/>

						<div className="user-meta">
							<span className="welcomeText">
								<span className="currentName">{welcomeName}</span>
							</span>
							<span className="currentTime">
								{/* Was "MMMM, DD, YYYY", which rendered "August, 31, 2026" with a
								    stray comma after the month. */}
								<Moment
									local
									format="MMMM DD, YYYY | "
								></Moment>
								<span>
									<Clock
										format={'hh:mm:ss A'}
										timeZone={'Asia/Kolkata'}
										ticking={true}
									/>
								</span>
							</span>
						</div>

						{/* data-bs-display="static" on the toggle below: Popper would otherwise
						    pin this menu to the "+" button with an inline transform that CSS
						    cannot cleanly override, and the menu needs to hang from the navbar
						    bottom edge like the notifications panel. */}
						<div className="dropdown-action">
							<a
								href="#"
								id="more-action"
								data-bs-toggle="dropdown"
								data-bs-display="static"
								aria-expanded="false"
							>
								<Button
									type="button"
									add
									className="btn-primary addBtn"
								/>
							</a>
							<div
								className="dropdown-menu dropdown-menu-end rounded-4 p-0"
								aria-labelledby="more-action"
							>
								<QuickAction
									user={userDetails.data}
									navigation={shortCutNavigate}
									suburl={suburl}
								/>
							</div>

							<ul
								className="dropdown-menu font-14"
								aria-labelledby="moreAction"
								style={{ minWidth: '240px' }}
							>
								<li>
									<a
										className="dropdown-item"
										href="#"
									>
										New project...
									</a>
								</li>
								<li>
									<a
										className="dropdown-item"
										href="#"
									>
										Settings
									</a>
								</li>
								<li>
									<a
										className="dropdown-item"
										href="#"
									>
										Profile
									</a>
								</li>
								<li>
									<hr className="dropdown-divider" />
								</li>
								<li>
									<a
										className="dropdown-item"
										onClick={handleSignout}
									>
										Sign out
									</a>
								</li>
							</ul>
						</div>
						<div
							className="d-inline"
							ref={notificationRef}
						>
							<div
								style={{ position: 'relative', cursor: 'pointer' }}
								onClick={() => {
									setIsOpenNotificationModal(!isOpenNotificationModal);
								}}
							>
								<img
									src={notification}
									alt="mdo"
									width="25"
									height="25"
								/>
								{unreadCount > 0 && (
									<span
										style={{
											display: 'flex',
											justifyContent: 'center',
											alignItems: 'center',
											position: 'absolute',
											top: 0,
											left: 10,
											background: '#F30104',
											color: 'white',
											borderRadius: '35%',
											fontSize: '10px',
											width: '25px',
											height: '20px',
										}}
									>
										{unreadCount > 99 ? '99+' : unreadCount}
									</span>
								)}
							</div>
							{isOpenNotificationModal && (
								<div className="notification-doggle-container">
									{/* py-3 rather than pt-4: there was no bottom padding, so the
								    header text sat flush on its divider. */}
									<div className="d-flex align-items-center border-bottom justify-content-between py-3 px-3 fs-4 font-bold flex-shrink-0">
										<p className="mb-0">Notifications</p>
										<p
											className="fs-5 font-semibold cursor-pointer"
											style={{ color: 'gray' }}
											onClick={() => {
												const ids = notifications
													.filter((data) => data.is_read === false)
													.map((item) => item.id);
												markReadNotification(ids);
											}}
										>
											Mark all as read
										</p>
									</div>
									{/* Only this middle section scrolls, so the header above and the
								    "View all notifications" link below stay visible. */}
									<div className="notification-doggle-list">
										{notifications.map((data) => (
											<div
												key={data.id}
												className="notification-doggle-item d-flex border-bottom gap-3 cursor-pointer"
												// minHeight, not height: a fixed 80px clipped any row whose
												// title wrapped to two lines once the text started wrapping.
												style={{
													minHeight: '80px',
													backgroundColor: data.is_read ? '' : '#daddec',
												}}
												onClick={() => {
													if (!data.is_read) {
														markReadNotification([data.id]);
													}
												}}
											>
												<div className="rounded-circle px-2 pt-1 flex-shrink-0">
													<img
														src={logo}
														alt="mdo"
														width="24"
														height="24"
														className="rounded-circle"
													/>
												</div>
												{/* Was a hard-coded width: 350px — the same width as the WHOLE
											    dropdown. Adding the avatar, the gap and the row padding on
											    top of that is what pushed content past the right edge.
											    Flexing to the space left over fixes it at any width;
											    min-width:0 lets it shrink below its text's natural width. */}
												<div style={{ flex: '1 1 auto', minWidth: 0 }}>
													<div className="d-flex gap-3 align-items-center justify-content-between">
														<div className="notification-toggle-title fs-5">{data.title}</div>
														<div
															className="fs-6 fw-semibold"
															style={{ color: 'gray' }}
														>
															{formatTimeAgo(data.created_at)}
														</div>
													</div>
													<div
														className="notification-truncate-text fs-10"
														style={{ color: 'gray' }}
													>
														{data.description}
													</div>
												</div>
											</div>
										))}
									</div>
									{/* Outside the scroll area, so it stays reachable without
								    scrolling to the bottom of a long list. */}
									<div className="text-center flex-shrink-0">
										<div
											className="fs-5 fw-semibold rounded-4  p-3 text-primary cursor-pointer"
											onClick={() => {
												setIsOpenNotificationModal(false);
												navigate(`/user/${params.suburl}/home`, {
													state: { tab: 'Notifications' },
												});
											}}
										>
											View all notifications
										</div>
									</div>
								</div>
							)}
						</div>

						<div className="dropdown text-end">
							<a
								href="#"
								style={{ padding: '4px 0' }}
								className="d-block link-dark text-decoration-none dropdown-toggle"
								id="dropdownUser1"
								data-bs-toggle="dropdown"
								aria-expanded="false"
							>
								{userDetails?.data?.organisation_logo !== '' &&
								userDetails?.data?.organisation_logo !== null &&
								userDetails?.data?.organisation_logo !== undefined ? (
									<img
										src={userDetails?.data?.organisation_logo}
										alt="mdo"
										width="32"
										height="32"
										className="rounded-circle me-2"
									/>
								) : (
									<span
										className="rounded-5 py-2 px-3 text-dark fs-4 fw-bold me-1"
										style={{ backgroundColor: '#8280FF' }}
									>
										{userDetails?.data?.first_name?.charAt(0)}
									</span>
								)}
							</a>
							<ul
								className="dropdown-menu font-14 rounded-5 p-0"
								aria-labelledby="dropdownUser1"
								style={{ minWidth: '200px' }}
							>
								<li>
									<a
										className="dropdown-item d-flex gap-4 align-items-center"
										onClick={() => {
											navigate(`/user/${suburl}/settings/profile-settings`);
										}}
									>
										{userDetails?.data?.organisation_logo !== '' &&
										userDetails?.data?.organisation_logo !== null &&
										userDetails?.data?.organisation_logo !== undefined ? (
											<img
												src={userDetails?.data?.organisation_logo}
												alt="mdo"
												width="30"
												height="30"
												className="rounded-circle me-2"
											/>
										) : (
											<div
												className="rounded-5 py-2 px-3 text-dark fs-4 fw-bold"
												style={{ backgroundColor: '#8280FF' }}
											>
												{userDetails?.data?.first_name?.charAt(0)}
											</div>
										)}
										<div>
											<div className="fs-4 font-medium">
												{userDetails?.data?.first_name + ' ' + userDetails?.data?.last_name}
											</div>
											<div className="fs-5 font-small text-gray">
												{userDetails?.data?.organisation_name || ''}
											</div>
										</div>
									</a>
								</li>
								<li>
									<a
										className="dropdown-item d-flex gap-4 align-items-center py-3 px-4"
										onClick={handleSignout}
									>
										<img
											src={logoutIcon}
											alt=""
											width={25}
											height={20}
										/>
										<div className="fs-4 fw-bold text-danger">Sign Out</div>
									</a>
								</li>
							</ul>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Navbar;
